import { useEffect } from 'react';
import { editingQuotePath, readEditingQuote } from '../quoteEdit';
import {
  initializeEmptyQuoteConfiguration,
  restoreQuoteConfiguration,
  syncCustomPaperFields,
  validateQuoteConfiguration,
} from '../quoteItem';
import { QUOTE_UPLOAD_ACCEPT, validateQuoteUploadFile } from '../quoteUploads';

function getTranslatedText(element, language) {
  return element?.getAttribute(language === 'ar' ? 'data-ar' : 'data-en') || '';
}

function selectedOptionText(select, language) {
  const option = select?.options?.[select.selectedIndex];
  if (!option) return '';
  return getTranslatedText(option, language) || option.textContent.trim();
}

function selectedCheckedText(control, language) {
  const text = control?.closest('label')?.querySelector('span');
  return getTranslatedText(text, language) || text?.textContent.trim() || control?.value || '';
}

function setSummaryText(configuration, id, text) {
  const element = configuration.querySelector(`#${id}`);
  if (element) element.textContent = text || '—';
}

function fileSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function setInputFiles(input, files) {
  const transfer = new DataTransfer();
  files.forEach((file) => transfer.items.add(file));
  input.files = transfer.files;
}

function renderUploadFiles(area, input, error = '') {
  let list = area.querySelector('.upload-file-list');

  if (!list) {
    list = document.createElement('div');
    list.className = 'upload-file-list';
    area.appendChild(list);
  }

  list.replaceChildren();

  if (error) {
    const message = document.createElement('p');
    message.className = 'upload-error';
    message.textContent = error;
    list.appendChild(message);
  }

  Array.from(input.files || []).forEach((file, index) => {
    const item = document.createElement('div');
    item.className = 'upload-file-item';

    const details = document.createElement('span');
    details.textContent = `${file.name} (${fileSize(file.size)})`;

    const remove = document.createElement('button');
    remove.className = 'upload-file-remove';
    remove.dataset.fileIndex = String(index);
    remove.type = 'button';
    remove.setAttribute('aria-label', `Remove ${file.name}`);
    remove.textContent = '\u00d7';

    item.append(details, remove);
    list.appendChild(item);
  });
}

function validUploadFiles(files) {
  const accepted = [];
  const rejected = [];

  files.forEach((file) => {
    const error = validateQuoteUploadFile(file);
    if (error) {
      rejected.push(error);
    } else {
      accepted.push(file);
    }
  });

  return {
    accepted,
    error: rejected[0] || '',
  };
}

function initUploadAreas(configuration) {
  const cleanups = Array.from(configuration.querySelectorAll('.upload-area')).map((area) => {
    const input = area.querySelector('input[type="file"]');
    if (!input) return () => {};
    input.accept = QUOTE_UPLOAD_ACCEPT;

    let hint = area.querySelector('.upload-hint');
    if (!hint) {
      hint = document.createElement('p');
      hint.className = 'upload-hint';
      area.querySelector('.upload-content')?.appendChild(hint);
    }
    const english = 'Supports: PDF, AI, EPS, PSD, JPG, JPEG, PNG, SVG, ZIP (Max 20MB each)';
    const arabic = 'يدعم: PDF, AI, EPS, PSD, JPG, JPEG, PNG, SVG, ZIP (الحد الأقصى 20 ميجابايت لكل ملف)';
    hint.setAttribute('data-en', english);
    hint.setAttribute('data-ar', arabic);
    hint.textContent = document.documentElement.lang === 'ar' ? arabic : english;

    const applyFiles = (files) => {
      const current = Array.from(input.files || []);
      const unique = [...current, ...files].filter((file, index, all) => (
        all.findIndex((candidate) => (
          candidate.name === file.name
          && candidate.size === file.size
          && candidate.lastModified === file.lastModified
        )) === index
      ));
      const { accepted, error } = validUploadFiles(unique);
      setInputFiles(input, accepted);
      renderUploadFiles(area, input, error);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const onChange = () => {
      const { accepted, error } = validUploadFiles(Array.from(input.files || []));
      setInputFiles(input, accepted);
      renderUploadFiles(area, input, error);
    };
    const onDragOver = (event) => {
      event.preventDefault();
      area.classList.add('dragover');
    };
    const onDragLeave = () => area.classList.remove('dragover');
    const onDrop = (event) => {
      event.preventDefault();
      area.classList.remove('dragover');
      applyFiles(Array.from(event.dataTransfer?.files || []));
    };
    const onRemove = (event) => {
      const button = event.target.closest('.upload-file-remove');
      if (!button) return;
      event.stopPropagation();
      const index = Number(button.dataset.fileIndex);
      setInputFiles(input, Array.from(input.files || []).filter((file, fileIndex) => fileIndex !== index));
      renderUploadFiles(area, input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    input.addEventListener('change', onChange);
    area.addEventListener('dragover', onDragOver);
    area.addEventListener('dragleave', onDragLeave);
    area.addEventListener('drop', onDrop);
    area.addEventListener('click', onRemove);

    return () => {
      input.removeEventListener('change', onChange);
      area.removeEventListener('dragover', onDragOver);
      area.removeEventListener('dragleave', onDragLeave);
      area.removeEventListener('drop', onDrop);
      area.removeEventListener('click', onRemove);
    };
  });

  return () => cleanups.forEach((cleanup) => cleanup());
}

function updateRequestSummary(configuration) {
  const language = document.documentElement.lang === 'ar' ? 'ar' : 'en';
  const activeSize = configuration.querySelector('.size-option.active');
  const activeSide = configuration.querySelector('.side-option.active');
  const activeColor = configuration.querySelector('.color-option.active');
  const activeCorner = configuration.querySelector('input[name="corners"]:checked');
  const activeFinish = configuration.querySelector('input[name="finishing"]:checked');
  const activeLamination = configuration.querySelector('input[name="lamination"]:checked');
  const customSize = configuration.querySelector('.custom-size-input')?.value.trim();
  const selectedSize = activeSize?.matches('[data-size="Other"], [data-size="Custom"]') && customSize
    ? customSize
    : activeSize?.dataset.size || getTranslatedText(activeSize, language);

  const activePocketType = configuration.querySelector('input[name="pocketType"]:checked');
  const activeAgendaType = configuration.querySelector('input[name="agendaType"]:checked');
  const activePersonalization = configuration.querySelector('input[name="personalization"]:checked');
  const activeShape = configuration.querySelector('input[name="shape"]:checked');
  
  setSummaryText(configuration, 'summarySize', selectedSize);
  setSummaryText(configuration, 'summarySides', getTranslatedText(activeSide, language) || activeSide?.textContent.trim());
  setSummaryText(configuration, 'summaryColor', getTranslatedText(activeColor, language) || activeColor?.textContent.trim());
  setSummaryText(configuration, 'summaryCorners', selectedCheckedText(activeCorner, language));
  setSummaryText(configuration, 'summaryFinish', selectedCheckedText(activeFinish, language));
  setSummaryText(configuration, 'summaryLamination', selectedCheckedText(activeLamination, language));
  setSummaryText(configuration, 'summaryPaper', selectedOptionText(configuration.querySelector('#paperType'), language));
  setSummaryText(configuration, 'summaryBinding', selectedOptionText(configuration.querySelector('#bindingType'), language));
  setSummaryText(configuration, 'summaryCover', selectedOptionText(configuration.querySelector('#coverPaper') || configuration.querySelector('#coverPaperType'), language));
  setSummaryText(configuration, 'summaryInside', selectedOptionText(configuration.querySelector('#insidePaper') || configuration.querySelector('#insidesPaperType'), language));
  setSummaryText(configuration, 'summaryPages', configuration.querySelector('#pageCount')?.value || configuration.querySelector('#insidePages')?.value);
  setSummaryText(configuration, 'summaryQuantity', configuration.querySelector('#quantity')?.value);
  setSummaryText(configuration, 'summaryPocketType', selectedCheckedText(activePocketType, language));
  setSummaryText(configuration, 'summaryAgendaType', selectedCheckedText(activeAgendaType, language));
  setSummaryText(configuration, 'summaryPersonalization', selectedCheckedText(activePersonalization, language));
  setSummaryText(configuration, 'summaryMaterial', selectedOptionText(configuration.querySelector('#cardMaterial'), language));
  setSummaryText(configuration, 'summaryShape', selectedCheckedText(activeShape, language));
}

function initProductConfiguration() {
  const configuration = document.querySelector('.configuration-section');
  if (!configuration) return undefined;
  const editingQuote = readEditingQuote();
  if (editingQuotePath(editingQuote) !== window.location.pathname) {
    initializeEmptyQuoteConfiguration(configuration);
  }
  const cleanupUploads = initUploadAreas(configuration);

  const onClick = (event) => {
    const uploadArea = event.target.closest('.upload-area');
    if (
      uploadArea
      && configuration.contains(uploadArea)
      && !event.target.matches('input[type="file"]')
      && !event.target.closest('.upload-file-remove')
    ) {
      uploadArea.querySelector('input[type="file"]')?.click();
      return;
    }

    const option = event.target.closest('.size-option, .side-option, .color-option, .folding-option, .folding-option-btn');
    if (!option || !configuration.contains(option)) return;

    const className = ['size-option', 'side-option', 'color-option', 'folding-option', 'folding-option-btn']
      .find((name) => option.classList.contains(name));
    configuration.querySelectorAll(`.${className}`).forEach((item) => item.classList.remove('active'));
    option.classList.add('active');
    updateRequestSummary(configuration);
  };

  const onInput = (event) => {
    const control = event.target;

    if (control.matches('input[name="nameOption"]')) {
      const multipleNamesFields = configuration.querySelector('#multipleNamesFields');
      if (multipleNamesFields) {
        multipleNamesFields.hidden = control.value !== 'multiple';
      }
    }

    syncCustomPaperFields(configuration);
    validateQuoteConfiguration(configuration, { showErrors: false });
    updateRequestSummary(configuration);
  };

  const onLanguageChange = () => updateRequestSummary(configuration);

  configuration.addEventListener('click', onClick);
  configuration.addEventListener('input', onInput);
  configuration.addEventListener('change', onInput);
  window.addEventListener('languagechange', onLanguageChange);
  updateRequestSummary(configuration);

  return () => {
    configuration.removeEventListener('click', onClick);
    configuration.removeEventListener('input', onInput);
    configuration.removeEventListener('change', onInput);
    window.removeEventListener('languagechange', onLanguageChange);
    cleanupUploads();
  };
}

export function usePageSetup({ title }) {
  useEffect(() => {
    document.title = title || 'INGAZ Print';
  }, [title]);

  useEffect(() => {
    const cleanupConfiguration = initProductConfiguration();
    const restoreEditingQuote = () => {
      const editingQuote = readEditingQuote();
      const configuration = document.querySelector('.configuration-section');

      if (configuration && editingQuotePath(editingQuote) === window.location.pathname) {
        restoreQuoteConfiguration(configuration, editingQuote);
      }
    };

    restoreEditingQuote();
    window.addEventListener('quoteedit', restoreEditingQuote);

    return () => {
      window.removeEventListener('quoteedit', restoreEditingQuote);
      cleanupConfiguration?.();
    };
  }, []);
}
